/* jshint esversion: 11 */
/* jshint ignore:start */

import axios from 'axios';
import { useContext, useEffect, useState, useCallback } from 'react';
import { MyContext } from '../Context/MyProvider';
import { showTost } from '../showTost';

const PaytmPayment = ({ Amount, ItemType, ItemId }) => {
  const { basUrl, setPayClose, setPlanId, setTransactionId, page, setBuyCoin, setButton } = useContext(MyContext);
  const [paymentData, setPaymentData] = useState(null);
  const [started, setStarted] = useState(false);
  const [opening, setOpening] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const [statusText, setStatusText] = useState("Preparing Paytm checkout...");
  const [openError, setOpenError] = useState("");

  const updatePaymentState = useCallback((status, transactionId) => {
    if (page === "Upgrade") {
      setPlanId(status);
      if (transactionId) setTransactionId(transactionId);
    } else if (page === "Wallet") {
      setPayClose(status);
      setButton(false);
    } else {
      setBuyCoin(status);
    }
  }, [page, setBuyCoin, setButton, setPayClose, setPlanId, setTransactionId]);

  useEffect(() => {
    if (!Amount || started) return;

    const createBackendOrder = async () => {
      setStarted(true);

      try {
        const localData = localStorage.getItem("Register_User");
        if (!localData) {
          throw new Error("Login required for payment.");
        }

        const userData = JSON.parse(localData);
        const itemType = ItemType || (page === "Upgrade" ? "plan" : page === "Wallet" ? "wallet" : "package");
        const itemId = ItemId || 0;

        const orderRes = await axios.post(`${basUrl}create_payment_order`, {
          uid: userData.id,
          item_type: itemType,
          item_id: itemId,
          amount: Amount,
          payment_gateway: "Paytm",
        });

        if (orderRes.data.Result !== "true") {
          throw new Error(orderRes.data.ResponseMsg || "Unable to create Paytm order.");
        }

        if (!orderRes.data.checkout || !orderRes.data.checkout.txnToken) {
          throw new Error("Paytm transaction token missing from backend.");
        }

        setPaymentData(orderRes.data);
      } catch (err) {
        showTost({ title: err.message || "Unable to start Paytm payment." });
        updatePaymentState("PaymentNotDone");
      }
    };

    createBackendOrder();
  }, [Amount, ItemId, ItemType, basUrl, page, started, updatePaymentState]);

  const verifyPaytmPayment = useCallback(async () => {
    try {
      setStatusText("Verifying Paytm payment...");
      const verifyRes = await axios.post(`${basUrl}verify_payment`, {
        order_id: paymentData.order_id,
        gateway_order_id: paymentData.checkout.gateway_order_id,
      });

      if (verifyRes.data.Result === "true" && verifyRes.data.status === "granted") {
        if (window.Paytm && window.Paytm.CheckoutJS && typeof window.Paytm.CheckoutJS.close === "function") {
          window.Paytm.CheckoutJS.close();
        }
        setOpening(false);
        setStatusText("Payment verified successfully.");
        updatePaymentState("PaymentVerified", paymentData.checkout.gateway_order_id);
      } else {
        throw new Error(verifyRes.data.ResponseMsg || "Paytm payment verification failed.");
      }
    } catch (err) {
      if (window.Paytm && window.Paytm.CheckoutJS && typeof window.Paytm.CheckoutJS.close === "function") {
        window.Paytm.CheckoutJS.close();
      }
      setOpening(false);
      showTost({ title: err.message || "Paytm payment verification failed." });
      updatePaymentState("PaymentNotDone");
    }
  }, [basUrl, paymentData, updatePaymentState]);

  const waitForPaytmCheckout = useCallback(() => {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (
          window.Paytm &&
          window.Paytm.CheckoutJS &&
          typeof window.Paytm.CheckoutJS.init === "function" &&
          typeof window.Paytm.CheckoutJS.invoke === "function"
        ) {
          window.clearInterval(timer);
          resolve();
          return;
        }

        if (Date.now() - startedAt > 8000) {
          window.clearInterval(timer);
          reject(new Error("Paytm Checkout SDK is not ready yet."));
        }
      }, 150);
    });
  }, []);

  const loadPaytmScript = useCallback((checkout) => {
    return new Promise((resolve, reject) => {
      if (
        window.Paytm &&
        window.Paytm.CheckoutJS &&
        typeof window.Paytm.CheckoutJS.init === "function" &&
        typeof window.Paytm.CheckoutJS.invoke === "function"
      ) {
        waitForPaytmCheckout().then(resolve).catch(reject);
        return;
      }

      const existing = document.getElementById("paytm-checkout-js");
      if (existing) {
        existing.remove();
      }

      window.Paytm = undefined;
      const script = document.createElement("script");
      script.id = "paytm-checkout-js";
      script.type = "text/javascript";
      script.crossOrigin = "anonymous";
      script.src = `${checkout.mode === "live" ? "https://securegw.paytm.in" : "https://securegw-stage.paytm.in"}/merchantpgpui/checkoutjs/merchants/${checkout.mid}.js`;
      script.onload = () => waitForPaytmCheckout().then(resolve).catch(reject);
      script.onerror = () => reject(new Error("Unable to load Paytm Checkout SDK."));
      document.body.appendChild(script);
    });
  }, [waitForPaytmCheckout]);

  const makePayment = useCallback(() => {
    if (!paymentData || !paymentData.checkout || opening) return;

    setOpening(true);
    setOpenError("");
    setStatusText("Opening Paytm checkout...");
    const checkout = paymentData.checkout;
    const config = {
      root: "",
      style: {
        bodyBackgroundColor: "#fafafb",
        themeBackgroundColor: "#0FB8C9",
        themeColor: "#ffffff",
        headerBackgroundColor: "#284055",
        headerColor: "#ffffff",
      },
      data: {
        orderId: checkout.gateway_order_id,
        token: checkout.txnToken,
        tokenType: "TXN_TOKEN",
        amount: checkout.amount
      },
      payMode: {
        order: ["CC", "DC", "NB", "UPI", "PPBL", "PPI", "BALANCE"]
      },
      website: checkout.website || "WEBSTAGING",
      flow: "DEFAULT",
      merchant: {
        mid: checkout.mid,
        redirect: false
      },
      handler: {
        transactionStatus: function () {
          verifyPaytmPayment();
        },
        notifyMerchant: function (eventName) {
          if (eventName === "APP_CLOSED") {
            if (window.Paytm && window.Paytm.CheckoutJS && typeof window.Paytm.CheckoutJS.close === "function") {
              window.Paytm.CheckoutJS.close();
            }
            setOpening(false);
            setStatusText("Paytm checkout closed.");
            updatePaymentState("PaymentNotDone");
          }
        }
      }
    };

    loadPaytmScript(checkout)
      .then(() => {
        if (
          !window.Paytm ||
          !window.Paytm.CheckoutJS ||
          typeof window.Paytm.CheckoutJS.init !== "function"
        ) {
          throw new Error("Paytm Checkout SDK is still loading. Please try again.");
        }
        return window.Paytm.CheckoutJS.init(config);
      })
      .then(function onSuccess() {
        if (
          !window.Paytm ||
          !window.Paytm.CheckoutJS ||
          typeof window.Paytm.CheckoutJS.invoke !== "function"
        ) {
          throw new Error("Paytm Checkout SDK is still loading. Please try again.");
        }
        setStatusText("Paytm checkout opened. Complete payment in the popup.");
        window.Paytm.CheckoutJS.invoke();
      })
      .catch(function onError(err) {
        console.error('[PAYTM SDK ERROR]', err);
        setOpening(false);
        setOpenError(err.message || "Unable to open Paytm checkout.");
        setStatusText("Paytm checkout did not open. Please try again.");
        showTost({ title: err.message || "Unable to open Paytm checkout." });
      });
  }, [loadPaytmScript, opening, paymentData, updatePaymentState, verifyPaytmPayment]);

  useEffect(() => {
    if (paymentData && !autoOpened) {
      setAutoOpened(true);
      makePayment();
    }
  }, [autoOpened, paymentData, makePayment]);

  return (
    <div id="BlockSection" className="px-[15px] py-[15px] w-full h-full fixed top-0 left-0 flex items-center justify-center bg-black bg-opacity-40 z-[999]">
      <div onClick={(e) => e.stopPropagation()} className="w-[320px] max-_430_:w-[92%] bg-white rounded-[8px] p-[18px] shadow-lg">
        <div className="flex flex-col items-center text-center gap-[12px]">
          <div className="w-[42px] h-[42px] rounded-full bg-[#e8f8fb] flex items-center justify-center text-[#00baf2] font-[700]">
            P
          </div>
          <div className="font-[700] text-[17px] text-[#1f2937]">Paytm</div>
          <div className="text-[14px] text-[#586174] leading-[20px]">{statusText}</div>
          {opening && (
            <div className="spinner-border spinner-border-sm text-info" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          )}
          {openError && (
            <div className="text-[12px] text-[#d33] leading-[18px]">{openError}</div>
          )}
          <button
            type="button"
            disabled={opening || !paymentData}
            onClick={makePayment}
            className={`w-full rounded-[8px] py-[10px] font-[700] text-white ${opening || !paymentData ? "bg-[#9aa8b5]" : "bg-[#00baf2]"}`}
          >
            {opening ? "Opening..." : openError ? "Try Again" : "Open Paytm"}
          </button>
          <button
            type="button"
            disabled={opening}
            onClick={() => updatePaymentState("PaymentNotDone")}
            className="text-[13px] text-[#697386] font-[600]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaytmPayment;
/* jshint ignore:end */
